/**
 * React Query hooks for widget theme management
 * Replaces ThemeContext with centralized cache that syncs across all widget instances
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchWidgetTheme,
  fetchPublishedWidgetTheme,
  isBrandAppV2WidgetThemeFallbackStatus,
  ThemeFontValue,
  ThemeIconValue,
  WidgetThemeFetchError,
} from '../api/widgetThemeApi';
import { getThemeCacheKey, useWidgetThemeParams } from '../utils/useWidgetThemeParams';

export const WIDGET_THEME_QUERY_KEY = ['bc-widget-theme'];
const MAX_WIDGET_THEME_RETRIES = 3;

// ============================================
// Cross-window theme sync via postMessage
// ============================================
export const THEME_SYNC_MESSAGE_TYPE = 'bc-theme-updated';

export type ThemeSyncPayload = {
  updateType: 'colors' | 'fonts' | 'icons' | 'all';
  themeId: string;
};

// Store current queryClient reference (updated by each hook call)
// This ensures the listener always uses the latest queryClient
let currentQueryClient: ReturnType<typeof useQueryClient> | null = null;
let isListenerRegistered = false;

/**
 * Sets up a singleton message listener for theme sync
 * Only registers once per window, but always uses the latest queryClient
 */
const useThemeSyncListener = (queryClient: ReturnType<typeof useQueryClient>) => {
  // Always update the current queryClient reference
  currentQueryClient = queryClient;

  useEffect(() => {
    // Only register listener once per window
    if (isListenerRegistered) return;
    isListenerRegistered = true;

    const handleMessage = (event: MessageEvent) => {
      // Only accept same-origin messages
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== THEME_SYNC_MESSAGE_TYPE) return;
      if (!currentQueryClient) return;

      const { themeId } = event.data.payload as ThemeSyncPayload;

      // Force refetch the theme query immediately using current queryClient
      currentQueryClient.refetchQueries({
        queryKey: [...WIDGET_THEME_QUERY_KEY, themeId],
      });
    };

    window.addEventListener('message', handleMessage);

    // Note: We intentionally don't remove listener on unmount
    // because other widgets may still need it, and it's a singleton
  }, []);
};

const shouldRetryWidgetThemeQuery = (failureCount: number, error: unknown) => {
  if (
    error instanceof WidgetThemeFetchError &&
    isBrandAppV2WidgetThemeFallbackStatus(error.status)
  ) {
    return false;
  }

  return failureCount < MAX_WIDGET_THEME_RETRIES;
};

/**
 * Fetch entire widget theme (colors + widget configs)
 * All widgets share this cached data
 */
export const useWidgetTheme = () => {
  const themeParams = useWidgetThemeParams();
  const queryClient = useQueryClient();
  const cacheKey = getThemeCacheKey(themeParams);

  // Set up cross-window sync listener (singleton - only runs once per window)
  useThemeSyncListener(queryClient);

  return useQuery({
    queryKey: [...WIDGET_THEME_QUERY_KEY, cacheKey],
    queryFn: async () => {
      if (themeParams.themeId) {
        return fetchWidgetTheme(themeParams.themeId);
      }
      const appId = window.location.hostname;
      // Same key as common-ui `V2CachedQueryKeys.AppBootstrapV2`. Checkout seeds
      // this before mounting widgets so we can skip the widget's bootstrap probe.
      const knownBrandAppV2 = queryClient.getQueryData(['appBootstrapV2']) != null;
      return fetchPublishedWidgetTheme(appId, { knownBrandAppV2 });
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: shouldRetryWidgetThemeQuery,
  });
};

/**
 * Get theme colors from the cached theme
 * Replaces the old useThemeColors from ThemeContext
 */
export const useThemeColors = () => {
  const { data: theme, isLoading, refetch } = useWidgetTheme();
  return {
    themeColors: (theme?.config?.colors || {}) as Record<string, string>,
    isLoading,
    refreshThemeColors: async () => {
      // Refetch the theme data to get updated colors
      await refetch();
    },
  };
};

/**
 * Get theme fonts from the cached theme
 * Returns fonts with name, url, and format for @font-face generation
 */
export const useThemeFonts = () => {
  const { data: theme, isLoading, refetch } = useWidgetTheme();
  return {
    themeFonts: (theme?.config?.fonts || {}) as Record<string, ThemeFontValue>,
    isLoading,
    refreshThemeFonts: async () => {
      // Refetch the theme data to get updated fonts
      await refetch();
    },
  };
};

/**
 * Get theme icons from the cached theme
 * Returns icons with name and url for custom icon rendering
 */
export const useThemeIcons = () => {
  const { data: theme, isLoading, refetch } = useWidgetTheme();
  return {
    themeIcons: (theme?.config?.icons || {}) as Record<string, ThemeIconValue>,
    isLoading,
    refreshThemeIcons: async () => {
      // Refetch the theme data to get updated icons
      await refetch();
    },
  };
};

/**
 * Get specific widget config from the cached theme
 * All instances with the same widgetId share this state
 */
export const useWidgetConfig = <T extends Record<string, any>>(
  widgetId: string | undefined,
  defaultState: T,
): T & { isLoading: boolean; savedKeys: Set<string> } => {
  const { data: theme, isLoading } = useWidgetTheme();
  const savedConfig = widgetId ? theme?.config?.widgetConfig?.[widgetId] : undefined;

  return {
    ...defaultState,
    ...savedConfig,
    isLoading,
    savedKeys: new Set(savedConfig ? Object.keys(savedConfig) : []),
  };
};

/**
 * Hook for editing widget config (updates cache immediately before save)
 * Changes are reflected across ALL widget instances with the same widgetId
 */
export const useWidgetEdit = (widgetId: string | undefined) => {
  const queryClient = useQueryClient();
  const themeParams = useWidgetThemeParams();
  const cacheKey = getThemeCacheKey(themeParams);

  const updateWidgetConfig = (key: string, value: any) => {
    if (!widgetId) return;

    // Update React Query cache immediately - ALL widgets re-render!
    queryClient.setQueryData([...WIDGET_THEME_QUERY_KEY, cacheKey], (oldData: any) => {
      if (!oldData) {
        // Initialize with minimal structure if no data exists
        return {
          config: {
            widgetConfig: {
              [widgetId]: {
                [key]: value,
              },
            },
          },
        };
      }

      return {
        ...oldData,
        config: {
          ...oldData?.config,
          widgetConfig: {
            ...oldData?.config?.widgetConfig,
            [widgetId]: {
              ...oldData?.config?.widgetConfig?.[widgetId],
              [key]: value,
            },
          },
        },
      };
    });
  };

  /**
   * Remove a single key from the widget config in cache (resets to component default)
   */
  const removeWidgetConfigKey = (key: string) => {
    if (!widgetId) return;

    queryClient.setQueryData([...WIDGET_THEME_QUERY_KEY, cacheKey], (oldData: any) => {
      if (!oldData?.config?.widgetConfig?.[widgetId]) return oldData;

      const { [key]: _, ...rest } = oldData.config.widgetConfig[widgetId];

      return {
        ...oldData,
        config: {
          ...oldData.config,
          widgetConfig: {
            ...oldData.config.widgetConfig,
            [widgetId]: rest,
          },
        },
      };
    });
  };

  /**
   * Get current widget state from cache (for saving)
   */
  const getWidgetState = (): Record<string, any> | undefined => {
    if (!widgetId) return undefined;

    const theme = queryClient.getQueryData([...WIDGET_THEME_QUERY_KEY, cacheKey]) as any;
    return theme?.config?.widgetConfig?.[widgetId];
  };

  return { updateWidgetConfig, removeWidgetConfigKey, getWidgetState };
};

// ── Studio Config Hooks ──

export interface StudioAvailability {
  conciergeIcon?: boolean;
  featureIconPrompts?: boolean;
  featureImagePrompts?: boolean;
  searchBar?: boolean;
  searchPrompts?: boolean;
  defaultSearchPrompts?: string[];
}

/**
 * Get studio availability settings from the theme response.
 * These control visibility of concierge UI elements.
 */
export const useStudioAvailability = (): StudioAvailability | undefined => {
  const { data: theme } = useWidgetTheme();
  return (theme as any)?.studioAvailability;
};

/**
 * Get studio agent name override from the theme response.
 * Takes priority over brandAppConfig.conciergeAgentName.
 */
export const useStudioAgentName = (): string | undefined => {
  const { data: theme } = useWidgetTheme();
  return (theme as any)?.studioAgentName;
};

export type StudioAgreementTemplate = 'inline' | 'centered-modal' | 'bottom-banner' | 'auto-accept';

export interface StudioAgreement {
  enabled: boolean;
  template?: StudioAgreementTemplate;
  title?: string;
  body?: string;
  termsOfUseUrl?: string;
  privacyPolicyUrl?: string;
  consumerHealthUrl?: string;
  contactEmail?: string;
  agreeButtonLabel?: string;
  version?: string;
}

/**
 * Get studio agreement/consent settings from the theme response.
 * When enabled, the first question in a new conversation is gated behind
 * a consent card until the user accepts.
 */
export const useStudioAgreement = (): StudioAgreement | undefined => {
  const { data: theme } = useWidgetTheme();
  return (theme as any)?.studioAgreement;
};

export interface StudioSupportSkills {
  customerLogin?: boolean;
  orderLookup?: boolean;
  orderCancellation?: boolean;
  returnProcessing?: boolean;
  refundProcessing?: boolean;
  accountAccess?: boolean;
  ticketLookup?: boolean;
  liveAgent?: boolean;
}

/**
 * Get support skill toggles from the Studio config mirrored onto the theme
 * response. Absent/false values mean the customer-facing capability is off.
 */
export const useStudioSupportSkills = (): StudioSupportSkills | undefined => {
  const { data: theme } = useWidgetTheme();
  return (theme as any)?.studioSupportSkills as StudioSupportSkills | undefined;
};

// ── Support Branding ──

/**
 * The brand's colors, copy and avatar for the support surfaces of the chat. It matches the backend
 * `StudioSupportBranding` shape on `ConciergeStudioConfig.supportBranding`.
 */
export interface StudioSupportBranding {
  softBackgroundHex: string;
  borderHex: string;
  accentDotHex: string;
  textHex: string;
  teamLabel: string;
  /** A caption under the composer. `{team}` and `{assistant}` stand for the team label and the assistant name. */
  pausedFooterCopy: string;
  takeoverPlaceholder: string;
  /** The row under a delivered shopper message. `{team}` stands for the team label. */
  deliveredCopy: string;
  teamAvatarUrl: string;
}

/**
 * The support branding that the brand saved in Concierge Studio, from the widget-theme cache. Undefined when the brand
 * saved none. Any field can be absent or empty. common-ui owns the defaults and fills them in (`supportBrandingOf`).
 */
export const useStudioSupportBranding = (): Partial<StudioSupportBranding> | undefined => {
  const { data: theme } = useWidgetTheme();
  return (theme as any)?.studioSupportBranding;
};

// ── Voice mode (Plan 1 → Plan 4) ──

// ---- VoiceSkills -----------------------------------------------------------

/**
 * Voice mode behavior knobs. Plan 1 ships just `enabled`; Plan 4 exposes the
 * full operational guardrail set (rate limits, recording, redaction, visitors).
 */
export interface StudioVoiceSkills {
  enabled: boolean;
  maxCallDurationMin: number;
  maxCallsPerHourPerUser: number;
  maxMinutesPerDayPerUser: number;
  maxCallsPerHourPerApp: number;
  inactivityWarnSec: number;
  inactivityEndSec: number;
  allowVisitors: boolean;
  recording: {
    enabled: 'always' | 'with_consent' | 'never';
    retentionDays: number;
  };
  transcriptRedaction: 'enabled' | 'disabled';
}

export const DEFAULT_STUDIO_VOICE_SKILLS: StudioVoiceSkills = {
  enabled: false,
  maxCallDurationMin: 10,
  maxCallsPerHourPerUser: 5,
  maxMinutesPerDayPerUser: 60,
  maxCallsPerHourPerApp: 1000,
  inactivityWarnSec: 30,
  inactivityEndSec: 60,
  allowVisitors: true,
  recording: { enabled: 'always', retentionDays: 90 },
  transcriptRedaction: 'enabled',
};

export const useStudioVoiceSkills = (): StudioVoiceSkills => {
  const { data: theme } = useWidgetTheme();
  const raw = (theme as any)?.studioVoiceSkills as Partial<StudioVoiceSkills> | undefined;
  if (!raw) return DEFAULT_STUDIO_VOICE_SKILLS;
  return {
    enabled: raw.enabled === true,
    maxCallDurationMin: raw.maxCallDurationMin ?? DEFAULT_STUDIO_VOICE_SKILLS.maxCallDurationMin,
    maxCallsPerHourPerUser: raw.maxCallsPerHourPerUser ?? DEFAULT_STUDIO_VOICE_SKILLS.maxCallsPerHourPerUser,
    maxMinutesPerDayPerUser: raw.maxMinutesPerDayPerUser ?? DEFAULT_STUDIO_VOICE_SKILLS.maxMinutesPerDayPerUser,
    maxCallsPerHourPerApp: raw.maxCallsPerHourPerApp ?? DEFAULT_STUDIO_VOICE_SKILLS.maxCallsPerHourPerApp,
    inactivityWarnSec: raw.inactivityWarnSec ?? DEFAULT_STUDIO_VOICE_SKILLS.inactivityWarnSec,
    inactivityEndSec: raw.inactivityEndSec ?? DEFAULT_STUDIO_VOICE_SKILLS.inactivityEndSec,
    allowVisitors: raw.allowVisitors ?? DEFAULT_STUDIO_VOICE_SKILLS.allowVisitors,
    recording: {
      enabled: raw.recording?.enabled ?? DEFAULT_STUDIO_VOICE_SKILLS.recording.enabled,
      retentionDays: raw.recording?.retentionDays ?? DEFAULT_STUDIO_VOICE_SKILLS.recording.retentionDays,
    },
    transcriptRedaction: raw.transcriptRedaction ?? DEFAULT_STUDIO_VOICE_SKILLS.transcriptRedaction,
  };
};

// ---- VoiceBranding ---------------------------------------------------------

/**
 * OpenAI Realtime voices exposed in Concierge Studio. `marin` and `cedar`
 * are OpenAI's recommended voices for `gpt-realtime-2`; the rest are the
 * legacy voice catalog.
 */
export type VoiceId = 'marin' | 'cedar' | 'alloy' | 'ash' | 'echo' | 'sage' | 'ballad' | 'verse' | 'shimmer' | 'coral';

/**
 * Visual presets for the on-stage voice blob. Replaces the legacy
 * `VoiceOrbStyle` (`spokes` / `bloom` / `pulse`) with the six gallery styles.
 */
export type VoiceBlobStyle = 'organic' | 'aurora' | 'particle' | 'waveform' | 'ripple' | 'metaball';

export const DEFAULT_VOICE_BLOB_STYLE: VoiceBlobStyle = 'aurora';

/**
 * @deprecated Voice studio cleanup (D-61) removed the per-voice color UI.
 * Type kept so back-compat reads of stored DDB rows still parse, but no
 * consumer should reference these fields anymore.
 */
export interface VoiceBrandingCustomProperties {
  orbPrimary?: string;
  orbHalo?: string;
  stageBgFrom?: string;
  stageBgTo?: string;
  statusLive?: string;
  statusConnecting?: string;
  statusError?: string;
  statusEnded?: string;
  muteBg?: string;
  muteFg?: string;
  endBg?: string;
  bubbleYou?: string;
  bubbleAssistant?: string;
}

// ─── Per-blob color schemas ─────────────────────────────────────────────────
//
// Each blob exposes bespoke color slots matching the visual primitives in
// `@brandclub/common-ui/Concierge/voice/blob/*`. These are the shared shape
// types for the FE: backend (`api-rewards`) carries equivalent (but
// independently defined) types on `VoiceBrandingVisual.blobColors`.
//
// All values are `#RRGGBB` hex. Backend validation is permissive
// (`z.string()`) so the FE picker output is accepted without transformation.

export interface AuroraColors {
  layer1: string;
  layer2: string;
  layer3: string;
  layer4: string;
}

export interface OrganicColors {
  highlight: string;
  primary: string;
  deep: string;
}

export interface ParticleColors {
  ring1: string;
  ring2: string;
  ring3: string;
  ring4: string;
}

export interface WaveformColors {
  core: string;
  ring1: string;
  ring2: string;
  ring3: string;
}

export interface RippleColors {
  highlight: string;
  mid: string;
  deep: string;
  ring: string;
}

export interface MetaballColors {
  from: string;
  to: string;
}

/** Discriminated map keyed by `VoiceBlobStyle` — both the studio form and
 *  the resolved theme payload carry the full map (defaults filled in by the
 *  mint endpoint), so callers can index by the active style without
 *  defensive checks. */
export interface BlobColorsByStyle {
  aurora: AuroraColors;
  organic: OrganicColors;
  particle: ParticleColors;
  waveform: WaveformColors;
  ripple: RippleColors;
  metaball: MetaballColors;
}

/** Bundled defaults — must match the backend's `DEFAULT_BLOB_COLORS` exactly
 *  so the FE renders identically whether the server fills in defaults or the
 *  client falls back. */
export const DEFAULT_BLOB_COLORS: BlobColorsByStyle = {
  aurora: { layer1: '#FF508C', layer2: '#78B4FF', layer3: '#64FFC8', layer4: '#FFC850' },
  organic: { highlight: '#FFFFFF', primary: '#7C5CFF', deep: '#1A0B4D' },
  particle: { ring1: '#FF7DD9', ring2: '#75ECFF', ring3: '#75AFFF', ring4: '#7575FF' },
  waveform: { core: '#FF8CC8', ring1: '#FF66A3', ring2: '#FF4D80', ring3: '#FF334D' },
  ripple: { highlight: '#D8FFF0', mid: '#2CE39A', deep: '#064D33', ring: '#2CE39A' },
  metaball: { from: '#FFAE00', to: '#FF2D75' },
};

/**
 * Voice persona + visual for the call surface. Voice studio cleanup (D-61)
 * narrowed `persona` to `voiceId` only — the `assistantName` is sourced
 * from the unified Identity tab via `useStudioAgentName()` (which mirrors
 * `personality.name`). The legacy `copy` block was removed entirely.
 *
 * Per-blob color customization (added in the voice-animation-colors work):
 * `visual.blobColors` carries the full 6-style map so the call overlay can
 * pick the slice matching the active `blobStyle` without merging defaults.
 */
export interface StudioVoiceBranding {
  persona: {
    assistantName: string;
    voiceId?: VoiceId;
  };
  visual?: {
    blobStyle?: VoiceBlobStyle;
    /** When false, hide the docked blob in the live voice overlay. Default true. */
    blobEnabled?: boolean;
    blobColors?: BlobColorsByStyle;
  };
}

export const DEFAULT_STUDIO_VOICE_BRANDING: StudioVoiceBranding = {
  persona: { assistantName: 'Benny' },
  visual: {},
};

export const useStudioVoiceBranding = (): StudioVoiceBranding => {
  const { data: theme } = useWidgetTheme();
  const raw = (theme as any)?.studioVoiceBranding as
    | {
        persona?: { voiceId?: VoiceId };
        visual?: {
          blobStyle?: VoiceBlobStyle;
          blobEnabled?: boolean;
          blobColors?: BlobColorsByStyle;
        };
      }
    | undefined;
  // D-61: assistantName is now sourced from the unified Identity tab,
  // mirrored onto the theme response as `studioAgentName`.
  const studioAgentName = (theme as any)?.studioAgentName as string | undefined;
  const assistantName = studioAgentName?.trim() || DEFAULT_STUDIO_VOICE_BRANDING.persona.assistantName;
  if (!raw) return { persona: { assistantName }, visual: { blobEnabled: true } };
  return {
    persona: {
      assistantName,
      voiceId: raw.persona?.voiceId,
    },
    visual: {
      blobStyle: raw.visual?.blobStyle,
      blobEnabled: raw.visual?.blobEnabled ?? true,
      blobColors: raw.visual?.blobColors,
    },
  };
};
